/* eslint-disable max-lines */
import {
  type AiTakeAVideoConfig,
  AttendanceVerificationMethod,
} from "@clipboard-health/contract-backend-main";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  Text,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ClockOutChartingReminder } from "@src/appV2/redesign/Charting/ClockOutChartingReminder";
import { useSkippableWait } from "@src/appV2/redesign/utils/useSkippableWait";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG } from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { useGetShiftFeedbackClockOutConfig } from "@src/appV2/Shifts/Shift/useGetShiftFeedbackClockOutConfig";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { SHIFT_DETAILS_FULL_PATH, SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH } from "../../path";
import type { ComplianceProofVideo } from "../TimeclockComplianceVideoProof";
import { TimeclockVideoProofFlow } from "../TimeclockComplianceVideoProof";
import { VideoValidationFailedBottomSheet } from "../TimeclockComplianceVideoProof/dialogs/VideoValidationFailedBottomSheet";
import { useValidateVideo } from "../TimeclockComplianceVideoProof/hooks/useValidateVideo";
import { AcceptNonInstantPayContent } from "./AcceptNonInstantPayContent";
import { ClockOutConfirmation } from "./ClockOutConfirmation";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "./constants";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";

export interface ClockOutWithVideoValidationBottomSheetProps {
  modalState: UseModalState;
  shift: WorkerShift;
  attendanceVerificationMethod: AiTakeAVideoConfig;
  workerCredentials: {
    pin: string;
  };
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  isMissedPunchRequestEnabled: boolean;
}

type TimeclockComplianceStep =
  | "charting-reminder"
  | "clock-out-confirm"
  | "video-recording"
  | "requesting-clock-out"
  | "clock-out-success"
  | "accept-non-instant-pay"
  | "requesting-non-instant-pay"
  | "video-validation-failed";

export function ClockOutWithVideoValidationBottomSheet(
  props: ClockOutWithVideoValidationBottomSheetProps
) {
  const {
    modalState,
    workerCredentials,
    workerId,
    workplaceId,
    shift,
    attendanceVerificationMethod,
    workplaceName,
    isMissedPunchRequestEnabled,
  } = props;

  const shiftId = shift._id;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const chartingReminderModalState = useModalState();
  const { chartingRecallEnabled: shouldShowChartingReminder } =
    useGetShiftFeedbackClockOutConfig(workplaceId);

  const firstStep: TimeclockComplianceStep = shouldShowChartingReminder
    ? "charting-reminder"
    : "clock-out-confirm";

  const [step, setStep] = useState<TimeclockComplianceStep>(firstStep);
  const complianceProofRef = useRef<ComplianceProofVideo>();

  useEffect(() => {
    if (modalState.modalIsOpen && step === "charting-reminder") {
      chartingReminderModalState.openModal();
    }
  }, [modalState.modalIsOpen, step, chartingReminderModalState]);

  const { mutateAsync: recordTimekeepingAction } = useRecordShiftTimekeepingAction();
  const { mutateAsync: validateVideo } = useValidateVideo();

  const { wait, skip: skipWait } = useSkippableWait();

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();

  const shiftDetailsPath = generatePath(SHIFT_DETAILS_FULL_PATH, {
    shiftId,
  });
  const submitTimesheetPath = `${shiftDetailsPath}${SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH}`;

  const closeBottomSheet = useCallback(() => {
    skipWait();
    modalState.closeModal();
    chartingReminderModalState.closeModal();
    setStep(firstStep);
  }, [modalState, chartingReminderModalState, skipWait, firstStep]);

  async function handleRecordClockOut(
    actionCheckType: ActionCheckType,
    videoProof: ComplianceProofVideo | undefined
  ) {
    setStep("requesting-clock-out");

    try {
      const postMutationShift = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_OUT,
        shiftActionCheck: actionCheckType,
        verificationMethod: AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
        complianceProof: videoProof,
      });

      setStep("clock-out-success");
      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);
      closeBottomSheet();

      queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
        result
          ? {
              ...result,
              response: {
                ...result.response,
                clockInOut: postMutationShift.data.clockInOut,
              },
            }
          : undefined
      );

      history.replace(submitTimesheetPath, { clockedOut: true });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock out, please try again.";

      logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_FAILED, {
        shiftId,
        workerId,
        workplaceId,
        clockOutType: "timeclock-compliance",
        error: errorMessage,
      });

      setStep("accept-non-instant-pay");
    }
  }

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.TIMECLOCK_VIDEO_PROOF_COMPLIANCE_FAILURE,
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      closeBottomSheet();
    }
  }

  function handleCancel() {
    closeBottomSheet();
  }

  if (step === "charting-reminder" && shouldShowChartingReminder) {
    return (
      <ClockOutChartingReminder
        modalState={chartingReminderModalState}
        shiftId={shiftId}
        workplaceId={workplaceId}
        onConfirmClockOut={() => {
          setStep("clock-out-confirm");
          chartingReminderModalState.closeModal();
        }}
        onCancel={() => {
          closeBottomSheet();
        }}
      />
    );
  }

  if (step === "clock-out-confirm" && modalState.modalIsOpen) {
    return (
      <ClockOutConfirmation
        modalState={modalState}
        onConfirm={() => {
          setStep("video-recording");
        }}
        onCancel={closeBottomSheet}
      />
    );
  }

  if (step === "video-recording") {
    return (
      <TimeclockVideoProofFlow
        workplaceName={workplaceName}
        timeclockLocation={attendanceVerificationMethod.timeclockLocationString}
        timeclockPin={workerCredentials.pin}
        timeclockGifUrl={attendanceVerificationMethod.timeclockGifUrl}
        workplaceId={workplaceId}
        shiftId={shiftId}
        workerId={workerId}
        clockAction="clock-out"
        videoConfig={DEFAULT_TIMECLOCK_COMPLIANCE_VIDEO_CONFIG}
        linearProgressDurationSeconds={20}
        isSkipTimeclockUsageEnabled={false}
        timeClockUsageWarning="Usage of the time clock is required to avoid payment delays for this shift."
        videoReviewNotice={
          <Text variant="body1">The video will be reviewed automatically by our system.</Text>
        }
        onSuccess={async (videoProof: ComplianceProofVideo) => {
          complianceProofRef.current = videoProof;

          try {
            const { isValid } = await validateVideo({
              shiftId,
              fileKey: videoProof.fileKey,
              bucketName: videoProof.bucketName,
            });

            if (isValid) {
              await handleRecordClockOut(ActionCheckType.LOCATION, videoProof);
            } else {
              setStep("video-validation-failed");
            }
          } catch (error: unknown) {
            const errorMessage =
              error instanceof Error ? error.message : "Failed to upload video, please try again.";

            showErrorToast(errorMessage);
            setStep("video-recording");
          }
        }}
        onCancel={() => {
          handleCancel();
        }}
      />
    );
  }

  return (
    <BottomSheet disableSwipeArea modalState={modalState} canDismiss={false}>
      {step === "requesting-clock-out" && (
        <BottomSheetLoadingContent
          title="Recording your clock out"
          description="This may take a moment"
        />
      )}

      {step === "clock-out-success" && (
        <TimekeepingActionSuccessContent
          title="Clocked out"
          description="We hope you had a great shift!"
          onClose={() => {
            skipWait();
          }}
        />
      )}

      {step === "accept-non-instant-pay" && (
        <AcceptNonInstantPayContent
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onAccept={async () => {
            setStep("requesting-non-instant-pay");
            await convertToNonInstantPay();
          }}
          onCancel={() => {
            setStep("video-recording");
          }}
        />
      )}

      {step === "video-validation-failed" && (
        <VideoValidationFailedBottomSheet
          modalState={modalState}
          onSeeInstructions={() => {
            setStep("video-recording");
          }}
          onCannotUseTimeclock={() => {
            setStep("accept-non-instant-pay");
          }}
        />
      )}

      {step === "requesting-non-instant-pay" && (
        <BottomSheetLoadingContent
          title="Converting to non-instant pay"
          description="This may take a moment"
        />
      )}
    </BottomSheet>
  );
}
/* eslint-enable max-lines */
